// ─── iPhone upload classifier ────────────────────────────────────────────────
// The editor's upload handler used to filter the incoming FileList with
// `ext-in-list || type.startsWith('image/')` and then `if (length === 0) return`
// — a SILENT drop with no UI feedback. On iOS that lost photos two ways:
//
//   1. A large HEIC multi-select can hand back an EMPTY FileList (a long-standing
//      WebKit quirk under memory pressure) — `change` fires with 0 files.
//   2. iCloud-optimised photos can arrive as ZERO-BYTE placeholders before the
//      device finishes downloading them from iCloud — present in the FileList
//      but with `size === 0` (and often an empty `type`).
//
// Either way the customer tapped Validate and saw nothing happen. This pure
// classifier replaces the silent drop with an explicit verdict the handler turns
// into visible feedback. It is dependency-free so the decision is unit-tested in
// isolation, the same way `uploadDedupe.ts` / `edgeSwipeDrawer.ts` are.

/** Extensions we treat as uploadable images. Superset of the editor's old list
 *  plus the common iOS/Android camera formats, so a file iOS hands back with an
 *  empty MIME type is still recognised by its extension. */
export const IMAGE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif', 'bmp', 'tif', 'tiff',
] as const;

/** Lowercased file extension, or '' when the name has none. Uses lastIndexOf so
 *  dotted filenames (`my.photo.heic`) resolve correctly. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/** Is this file an image we accept? True if the MIME type is `image/*` OR the
 *  extension is a known image format. The OR matters on iOS, where the picker
 *  often delivers HEIC with an empty `type` (extension saves it) or a converted
 *  JPEG named generically (MIME saves it). */
export function looksLikeImage(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  return (IMAGE_EXTENSIONS as readonly string[]).includes(fileExtension(file.name));
}

export interface UploadSelectionVerdict {
  /** Image files with content — these get uploaded. */
  accepted: File[];
  /** Zero-byte image-ish files — iCloud placeholders still downloading. Worth a
   *  retry once the device finishes, so we surface a "try again" message rather
   *  than dropping them. */
  notReady: File[];
  /** Has content but isn't an image we accept. */
  rejected: File[];
  /** The FileList itself was empty (iOS large-multi-select bug). */
  isEmptyInput: boolean;
}

/**
 * Classify a raw file selection into buckets the handler can act on with
 * visible feedback, instead of silently returning when nothing passes a filter.
 */
export function classifyUploadSelection(input: FileList | File[]): UploadSelectionVerdict {
  const files = Array.from(input);
  const accepted: File[] = [];
  const notReady: File[] = [];
  const rejected: File[] = [];

  for (const f of files) {
    const image = looksLikeImage(f);
    if (f.size === 0) {
      // Zero bytes: either an iCloud placeholder not yet downloaded (image-ish
      // name/type → retryable) or genuine junk (→ reject).
      if (image) notReady.push(f);
      else rejected.push(f);
      continue;
    }
    if (image) accepted.push(f);
    else rejected.push(f);
  }

  return { accepted, notReady, rejected, isEmptyInput: files.length === 0 };
}

// ─── Shared user-facing feedback ──────────────────────────────────────────────
// Every upload entry point (editor, magic link, photo-upload page, in-flow
// step) used to `return` silently when nothing passed its filter. On iOS that
// looked like "I tapped upload and nothing happened" and is the leading cause
// of orders that show 0 photos in admin. `uploadNoticeFor` is the ONE place
// that turns a verdict into a message, so the four paths can't drift apart.

/** Shown when the FileList came back empty — the iOS large-multi-select bug. */
export const UPLOAD_MSG_EMPTY =
  'Aucune photo reçue. Sur iPhone, réessayez en sélectionnant moins de photos à la fois.';
/** Shown when every selected image is still a zero-byte iCloud placeholder. */
export const UPLOAD_MSG_NOT_READY =
  'Vos photos sont encore en téléchargement depuis iCloud. Patientez un instant puis réessayez.';
/** Shown when the selection has content but none of it is an image we accept. */
export const UPLOAD_MSG_NO_IMAGE =
  'Aucune image valide dans la sélection (formats acceptés : JPG, PNG, WebP, HEIC).';

export interface UploadNoticeResult {
  /** True when at least one file is ready to upload — the caller proceeds. */
  proceed: boolean;
  /** Message to show when nothing can be uploaded (proceed === false). */
  blocked: string | null;
  /** Non-blocking heads-up when we proceed but skipped iCloud placeholders. */
  partial: string | null;
}

/**
 * Map a classifier verdict to visible feedback. Replaces the silent `return`
 * each upload path used to do, so a customer always sees why an upload did or
 * didn't start.
 */
export function uploadNoticeFor(verdict: UploadSelectionVerdict): UploadNoticeResult {
  const proceed = verdict.accepted.length > 0;
  if (!proceed) {
    let blocked: string;
    if (verdict.isEmptyInput && verdict.notReady.length === 0) blocked = UPLOAD_MSG_EMPTY;
    else if (verdict.notReady.length > 0) blocked = UPLOAD_MSG_NOT_READY;
    else blocked = UPLOAD_MSG_NO_IMAGE;
    return { proceed: false, blocked, partial: null };
  }
  const partial = verdict.notReady.length > 0
    ? `${verdict.notReady.length} photo(s) encore sur iCloud ont été ignorées — réessayez plus tard pour les ajouter.`
    : null;
  return { proceed: true, blocked: null, partial };
}
